import {
  AppRegistration,
  CheckCircle,
  DataUsage,
  Devices,
  Inventory,
} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { MEASURE_TYPES, OURA_STATE_STATUS } from "../../utils/constants";
import { get, post } from "../../utils/io";
import { usePagination } from "../hooks";
import MainContentContainer from "../Layouts/MainContentContainer";
import StelDataGrid from "../StelDataGrid";
import DeviceFilter from "./DeviceSearchForm/SearchFormMobile";
import Heart from "components/icons/Heart";

const emptySearchInputs = {
  macAddress: "",
  serialNumber: "",
  status: "",
  type: "",
  hubId: "",
  isBlocked: false,
  includeImage: true,
};

const initialTotalCountStatus = {
  total_count: 0,
  counts_by_status: {
    inventory: 0,
    registered: 0,
    running: 0,
    expired: 0,
  },
};

function OuraRing() {
  const history = useHistory();
  const { hubId } = useParams();
  const [loading, setLoading] = useState(false);
  const { paginationModel, setPaginationModel } = usePagination();
  const [devices, setDevices] = useState([]);
  const [searchInputs, setSearchInputs] = useState({
    ...emptySearchInputs,
    hubId: hubId || "",
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalCountStatus, setTotalCountStatus] = useState(
    initialTotalCountStatus
  );
  const [ouraStelModelIds, setOuraStelModelIds] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loadingDataRetrieve, setLoadingDataRetrieve] = useState(false);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);

  const handleBackClick = () => {
    if (!hubId) return;
    history.push(`/hubs/${hubId}`);
  };

  const getDevices = useCallback(async () => {
    setLoading(true);
    const payload = {
      mac_address: searchInputs?.macAddress,
      is_blocked: searchInputs?.isBlocked,
      serial_number: searchInputs?.serialNumber,
      make_id: searchInputs?.makeId,
      state: searchInputs?.status
        ? JSON.stringify({ status: searchInputs?.status.toUpperCase() })
        : null,
      model_ids: ouraStelModelIds,
      bound_hub_id: searchInputs?.hubId,
      offset: paginationModel.pageSize * paginationModel.page,
      limit: paginationModel.pageSize,
      include_image_url: false,
    };

    const res = await get("/devices", payload);
    if (res?.status === 200) {
      setDevices(res.data.devices);
      setRowCount(res.data.total_count);
    } else {
      enqueueSnackbar("Unable to retrieve devices", {
        variant: "error",
      });
    }
    setLoading(false);
  }, [searchInputs, ouraStelModelIds, paginationModel]);

  const fetchOuraSession = useCallback(async () => {
    const res = await get(`hubs/${hubId}/oura/session`);

    const isValidResponse = res.status === 200 && res.data.status === "active";
    if (!isValidResponse) return;

    if (res.data.device_id) {
      getDevices();
    } else {
      setTimeout(fetchOuraSession, 3000);
    }
  }, [hubId, getDevices]);

  const onDataRetrieval = async () => {
    setLoadingDataRetrieve(true);
    const res = await post(`hubs/${hubId}/oura/data_retrieval`);
    if (res.status !== 200) {
      enqueueSnackbar("Failed to send data retrieval command to Hub", {
        variant: "error",
      });
    } else {
      enqueueSnackbar("Hub will retrieve data from Oura Ring", {
        variant: "success",
      });
      fetchOuraSession();
    }
    setLoadingDataRetrieve(false);
  };

  const onConnectOura = async () => {
    setLoadingConnect(true);
    const res = await post(`hubs/${hubId}/oura/connect`);
    if (res.status !== 200) {
      enqueueSnackbar("Failed to send connection command to hub", {
        variant: "error",
      });
    } else {
      enqueueSnackbar("Hub can now connect to Oura Ring", {
        variant: "success",
      });
    }
    setLoadingConnect(false);
  };

  const onDisconnectOura = async () => {
    setLoadingDisconnect(true);
    const res = await post(`hubs/${hubId}/oura/disconnect`);
    if (res.status !== 200) {
      enqueueSnackbar("Failed to send disconnection command to hub", {
        variant: "error",
      });
    } else {
      enqueueSnackbar("Hub can now disconnect from Oura Ring", {
        variant: "success",
      });
    }
    setLoadingDisconnect(false);
  };

  const deviceStatusInfo = useMemo(
    () => [
      {
        label: "Total Count",
        value: totalCountStatus.total_count,
        icon: <Devices />,
        isHighlight: true,
      },
      {
        label: "In Inventory",
        value: totalCountStatus.counts_by_status.inventory,
        icon: <Inventory />,
      },
      {
        label: "Bound",
        value: totalCountStatus.counts_by_status.bound,
        icon: <AppRegistration />,
      },
      {
        label: "Running",
        value: totalCountStatus.counts_by_status.running,
        icon: <DataUsage />,
      },
      {
        label: "Completed",
        value: totalCountStatus.counts_by_status.expired,
        icon: <CheckCircle />,
      },
    ],
    [totalCountStatus]
  );

  useEffect(() => {
    // Don't find devices until `ouraStelModelIds` is set and is not empty
    if (ouraStelModelIds && ouraStelModelIds.length > 0) {
      getDevices();
      fetchOuraSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputs, paginationModel, ouraStelModelIds]);

  useEffect(() => {
    const getOuraStats = async () => {
      const res = await get("/oura/stats");
      if (res?.status === 200) {
        setTotalCountStatus(res.data);
      }
    };
    getOuraStats();
  }, []);

  useEffect(() => {
    const setOuraModelId = async () => {
      const res = await get("/device_models");
      const ids = res.data.device_models
        .filter((model) => model.name === "Oura Ring" && model.make === "Oura")
        .map((model) => model.id);

      setOuraStelModelIds(ids);
    };
    setOuraModelId();
  }, []);

  const DataGridColumns = [
    {
      field: "serial_number",
      headerName: "Serial Number",
      renderCell: (params) => params?.row?.serial_number,
      ...(isMobile ? { width: 120 } : { flex: 0.8 }),
    },
    {
      field: "status",
      ...(isMobile ? { width: 100 } : { flex: 0.7 }),
      headerName: "Status",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row.status && (
            <OuraRingStatusIndicator status={params.row.status} />
          )}
        </Box>
      ),
    },
    {
      field: "last_used",
      ...(isMobile ? { width: 200 } : { flex: 1 }),
      headerName: "Last Used",
      renderCell: (params) =>
        params.row.last_used_time
          ? new Date(params.row.last_used_time).toLocaleString()
          : "",
    },
    {
      field: "bound_hub",
      ...(isMobile ? { width: 150 } : { flex: 0.8 }),
      headerName: "Bound Hub",
    },
    {
      field: "mac_address",
      ...(isMobile ? { width: 150 } : { flex: 0.8 }),
      headerName: "MAC Address",
    },
    {
      field: "type",
      ...(isMobile ? { width: 100 } : { flex: 0.7 }),
      headerName: "Type",
      renderCell: (params) => MEASURE_TYPES[params.row?.type]?.label,
    },
    {
      field: "make_model",
      ...(isMobile ? { width: 100 } : { flex: 0.7 }),
      headerName: "Make/Model",
      renderCell: (params) =>
        `${params.row.make || ""}/${params.row?.model_name || ""}`,
    },
  ];

  return (
    <MainContentContainer
      title={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          flexWrap="wrap"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "100%",
                minWidth: 0,
              }}
              onClick={handleBackClick}
            >
              <ChevronLeftIcon sx={{ color: "#1F79E2" }} />
            </Button>
            <Typography
              component="h1"
              sx={{ fontSize: 24, fontWeight: 700, margin: 0 }}
            >
              {hubId} - Oura Ring
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={onConnectOura}
              loading={loadingConnect}
              variant="contained"
              sx={{ fontSize: 12, fontWeight: 600 }}
            >
              Connect
            </Button>
            <Button
              onClick={onDisconnectOura}
              loading={loadingDisconnect}
              variant="contained"
              color="error"
              sx={{ fontSize: 12, fontWeight: 600 }}
            >
              Disconnect
            </Button>
          </Box>
        </Box>
      }
      analyticsTitle="Live Devices"
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          sx={{ textTransform: "uppercase", fontSize: 12, fontWeight: 600 }}
          onClick={onDataRetrieval}
          startIcon={<Heart />}
          loading={loadingDataRetrieve}
        >
          Data retrieval
        </Button>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 2,
          width: "100%",
          marginBottom: 3,
        }}
      >
        {deviceStatusInfo.map((info) => (
          <Box
            key={info.label}
            sx={{
              gridColumn: {
                xs: info.isHighlight ? "1 / -1" : "auto",
                sm: "auto",
              },
              border: info.isHighlight ? "none" : "1px solid #E6E6EC",
              background: info.isHighlight
                ? "linear-gradient(135deg, #209BFC 0%, #518DEE 100%)"
                : "transparent",
              p: 3,
              borderRadius: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: info.isHighlight ? "#FFFFFF" : "#667085",
                }}
              >
                {info.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: info.isHighlight ? "#FFFFFF" : "inherit",
                }}
              >
                {info.value}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                backgroundColor: info.isHighlight
                  ? "rgba(255, 255, 255, 0.2)"
                  : "#1F79E20D",
                color: info.isHighlight ? "#FFFFFF" : "#1F79E2",
                p: 1,
                borderRadius: "50%",
              }}
            >
              {info.icon}
            </Box>
          </Box>
        ))}
      </Box>
      <DeviceFilter onSubmit={setSearchInputs} isSearchDisabled={loading} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#1F79E20D",
          padding: "4px 8px",
          borderRadius: 2,
          border: "1.5px solid #1F79E226",
          width: "fit-content",
        }}
      >
        <ErrorIcon sx={{ color: "#1F79E2", width: 20, height: 20 }} />
        <Typography sx={{ color: "#1F79E2", fontSize: 12, p: 0, m: 0 }}>
          If you notice any disabled devices listed below, please go to the Hub
          page to synchronize the data with your Oura Ring.
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        <StelDataGrid
          loading={loading}
          columns={DataGridColumns}
          rows={devices}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          paginationMode="server"
          isRowSelectable={(params) => !!params.row.bound_hub}
          getRowClassName={(params) =>
            !params.row.bound_hub ? "datagrid-row-disabled" : ""
          }
          onRowClick={(params) => {
            if (params.row.bound_hub) {
              history.push(
                `/hubs/${params.row.bound_hub}/oura-devices/${params.id}`
              );
            }
          }}
          sx={{
            "& .MuiDataGrid-columnHeader": { backgroundColor: "#F1F1F1" },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .datagrid-row-disabled": {
              opacity: 0.5,
              cursor: "not-allowed",
              pointerEvents: "none",
            },
          }}
        />
      </Box>
    </MainContentContainer>
  );
}

export function OuraRingStatusIndicator(props) {
  const { status } = props;
  const statusConfig = OURA_STATE_STATUS[status.toUpperCase()];
  return (
    <Typography
      sx={{
        textAlign: "center",
        fontWeight: 500,
        fontSize: 14,
        color: statusConfig.color,
        border: `1.5px solid ${statusConfig.border}`,
        borderRadius: 2,
        padding: "5px 6px",
        backgroundColor: statusConfig.background,
        width: "fit-content",
      }}
    >
      {statusConfig.label}
    </Typography>
  );
}

OuraRingStatusIndicator.propTypes = {
  status: PropTypes.string.isRequired,
};

export default OuraRing;
